import { motion, useReducedMotion } from "framer-motion";
import { AgentAvatar } from "./AgentAvatar";
import type { Persona } from "../OnboardingFlow";

/**
 * Beyond Sure — alive glass empty state (Chat).
 *
 * The brief: "the empty state should breathe — slow aurora gradient
 * behind a glass card, not a static cream wall." This delivers that
 * without going full bling:
 *
 *   • Three soft brand-tinted blobs at low opacity, slowly drifting
 *     (~14s loop). Reduced motion → no drift, fixed positions.
 *   • A glass card carrying the avatar, greeting, tagline, and the
 *     starter-prompt slot. Backdrop-blur over the aurora gives the
 *     same calm depth as the login screen.
 *   • Soft top vignette to keep the focus near eye level.
 *
 * Everything is purely additive — the parent ChatScreen still controls
 * layout. The component is fully self-contained: drop it where the old
 * EmptyState was, no API changes.
 *
 * a11y:
 *   - The aurora is `aria-hidden` (decorative).
 *   - The card is the actual landmark — the agent avatar is decorative
 *     too; the headline carries the page semantics.
 *   - Tagline + starter slots are inside the same card so screen readers
 *     announce them as one cohesive welcome.
 */

interface EmptyStateProps {
  /** First name from onboarding answers, if available. */
  userName?: string;
  /** Persona from onboarding answers — drives the tagline. */
  persona?: Persona | null;
  /** Phase 3 slot for tailored starter prompts. */
  children?: React.ReactNode;
}

export function EmptyState({ userName, persona, children }: EmptyStateProps) {
  const greeting = greetingFor(userName);
  const tagline = taglineFor(persona ?? null);
  const reducedMotion = useReducedMotion();

  return (
    <div
      className="relative flex-1 flex items-center justify-center px-5 py-12 overflow-hidden"
      data-testid="chat-empty-state"
    >
      {/* ── Aurora layer — decorative ─────────────────────────────── */}
      <AuroraBackground reducedMotion={reducedMotion ?? false} />

      {/* ── Glass card ────────────────────────────────────────────── */}
      <motion.div
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, ease: [0.4, 0, 0.2, 1] }}
        className={[
          "relative z-10 w-full max-w-[640px]",
          "px-6 sm:px-10 py-9 sm:py-12",
          "rounded-[28px]",
          // Glass: same family as the login form panel.
          "bg-[rgb(255_252_245_/_0.62)]",
          "backdrop-blur-[18px] backdrop-saturate-[1.35]",
          "border border-[rgb(255_255_255_/_0.55)]",
          "shadow-[0_24px_64px_-32px_rgba(20,75,51,0.18),0_2px_8px_-4px_rgba(0,0,0,0.04)]",
          "flex flex-col items-center text-center",
        ].join(" ")}
      >
        <AgentAvatar size={64} />
        <h1
          className={[
            "mt-6 bsure-display",
            "text-[28px] sm:text-[34px] leading-[1.1] font-bold",
            "text-[var(--color-ink)]",
            "tracking-[-0.01em]",
          ].join(" ")}
        >
          {greeting}
        </h1>
        <p
          className={[
            "mt-3.5 text-[15px] sm:text-[15.5px] leading-[1.55]",
            "max-w-[480px] text-[var(--color-ink-60)]",
          ].join(" ")}
        >
          {tagline}
        </p>

        {/* Starter prompts (Phase 3 slot). */}
        {children ? <div className="mt-7 w-full">{children}</div> : null}
      </motion.div>
    </div>
  );
}

/* ────────────────── Aurora layer ────────────────── */

function AuroraBackground({ reducedMotion }: { reducedMotion: boolean }) {
  // Three blobs, layered with mix-blend → multiply for the "rich but
  // calm" feel. The brand-deep green keeps it on-voice; the warm gold
  // hint adds dimension without going pastel-bright.
  const drift = reducedMotion
    ? undefined
    : {
        // Subtle diagonal drift, 14s loop. Each blob offset so they
        // never sit on top of each other → no flat spots.
        x: ["0%", "3%", "-2%", "0%"],
        y: ["0%", "-4%", "3%", "0%"],
        scale: [1, 1.06, 0.98, 1],
      };

  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{ contain: "paint" }}
    >
      <motion.div
        className="absolute -top-[20%] -left-[10%] w-[60vw] h-[60vw] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(20,75,51,0.18) 0%, rgba(20,75,51,0) 60%)",
        }}
        animate={drift}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-[20%] -right-[15%] w-[55vw] h-[55vw] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(199,132,17,0.14) 0%, rgba(199,132,17,0) 60%)",
        }}
        animate={
          reducedMotion
            ? undefined
            : {
                x: ["0%", "-3%", "2%", "0%"],
                y: ["0%", "3%", "-2%", "0%"],
                scale: [1, 1.04, 1.02, 1],
              }
        }
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-[30%] left-[40%] w-[40vw] h-[40vw] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(30,144,255,0.08) 0%, rgba(30,144,255,0) 60%)",
        }}
        animate={
          reducedMotion
            ? undefined
            : {
                x: ["0%", "2%", "-3%", "0%"],
                y: ["0%", "-3%", "2%", "0%"],
              }
        }
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Soft top vignette so the headline reads on top of the aurora. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(250,249,247,0.55) 0%, rgba(250,249,247,0) 30%, rgba(250,249,247,0) 70%, rgba(250,249,247,0.4) 100%)",
        }}
      />
    </div>
  );
}

function greetingFor(name?: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "How can I help today?";
  // Personable but not saccharine — matches BeyondSure's calm voice.
  return `Hi ${trimmed} — what's on your mind?`;
}

function taglineFor(persona: Persona | null): string {
  switch (persona) {
    case "agent":
    case "agency":
    case "bfsi":
      return "I can compare plans for your clients, audit existing policies, draft renewal pitches, and surface what's renewing this month.";
    case "customer":
    default:
      return "I compare insurers, audit your existing policies, and explain the fine print — no commission, plain language.";
  }
}

export default EmptyState;
